import { useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Send, MapPin, Phone } from 'lucide-react';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', math_answer: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const math = useMemo(() => ({ a: rand(2, 9), b: rand(2, 9) }), []);
  const { t } = useLanguage();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message || !form.math_answer) {
      toast.error(t('contact.fillAll', 'Please fill in all fields'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/contact', {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        math_a: math.a,
        math_b: math.b,
        math_answer: Number(form.math_answer),
      });
      toast.success(t('contact.sentToast', "Message sent — we'll reply soon"));
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '', math_answer: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('contact.sendFailed', 'Failed to send'));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <SEO title="Contact Us" description="Get in touch with the Jwalek team. Questions, feedback, or bulk order inquiries welcome." path="/contact" />
      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('contact.title', 'Get in touch')}</h1>
          <p className="mt-2" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.subtitle', "Questions, feedback or bulk-order inquiries — we'd love to hear from you.")}</p>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-orange-400" /><span style={{ color: 'var(--jw-text-muted)' }}>hello@jwalek.com</span></div>
            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-pink-400" /><span style={{ color: 'var(--jw-text-muted)' }}>+966 11 000 0000</span></div>
            <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-fuchsia-400" /><span style={{ color: 'var(--jw-text-muted)' }}>{t('footer.address', 'Riyadh, Kingdom of Saudi Arabia')}</span></div>
          </div>
        </div>
        <Card className="jw-card p-6">
          {sent ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full jw-gradient-bg grid place-items-center mx-auto"><Send className="w-6 h-6 text-white" /></div>
              <h2 className="font-display text-2xl font-bold mt-4">{t('contact.thanks', 'Thanks for reaching out!')}</h2>
              <p className="mt-2" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.thanksDesc', 'We received your message and will get back to you soon.')}</p>
              <Button onClick={() => setSent(false)} data-testid="contact-another-button" className="mt-6 jw-btn-primary">{t('contact.sendAnother', 'Send another')}</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.name', 'Name')}</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.email', 'Email')}</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.subject', 'Subject')}</label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" className="mt-1" />
              </div>
              <div>
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.message', 'Message')}</label>
                <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className="mt-1" />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.human', "To prove you're human: what is")} <b>{math.a} + {math.b}</b>?</label>
                  <Input type="number" value={form.math_answer} onChange={(e) => setForm({ ...form, math_answer: e.target.value })} data-testid="contact-math-input" className="mt-1 max-w-32" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit-button" className="jw-btn-glossy rounded-[12px] h-11 px-6">
                  <Send className="w-4 h-4 mr-2" /> {loading ? t('contact.sending', 'Sending...') : t('contact.send', 'Send message')}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;
